import { headerAndFooter, constants } from '@config';

import AbstractElement from './abstractElement';
import Text from './text';

export default class Textarea extends AbstractElement {
  constructor (parameters) {
    super();
    // attributes
    this.id = null;
    this.cls = 'element textarea';
    this.x = 0;
    this.y = 0;
    this.width = 100;
    this.height = 100;
    this.extra_width_factor = 0.01;
    this.library_text = false;
    this.first_load = false;
    this.new_rect_height = null;
    this.new_rect_bottom = null;
    this.text_box_height = null;
    this.smart_asset = null;
    this.heading = null;
    this.meta = null;
    this.typing = null;
    this.definedContentType = null;

    this.parent_text = null;
    this.child_text = null;
    this.page_id = INKIVE.Tool._current_design_id;

    this.setOriginalFontSizeFromClass();

    this.setValues(parameters);

    this.text = new Text(parameters ? parameters.text : {});
  }

  setOriginalFontSizeFromClass () {
    if (this.cls.indexOf('subelement') > -1) {
      const found = this.cls.match(/original-font-size-(\d*\.\d+|\d+)/);
      if (found) {
        this.original_font_size = parseFloat(found[1]);
      }
    }
  }

  fixOpacity () {
    const $dom = $(this.dom);
    const opacity = $dom.attr('opacity');

    parseFloat(opacity) ?? $dom.removeAttr('opacity');
  }

  setDOM (dom, checkCached = true, shouldSkipGetChars) {
    if (!dom || !$(dom).is('g.textarea')) return null;

    const id = dom.getAttribute('id');

    // check in cache
    if (
      checkCached &&
      INKIVE.Text._cache[id] &&
      INKIVE.Text._cache[id].dom === dom
    ) {
      return INKIVE.Text._cache[id];
    }
    this.dom = dom;
    this.heading = dom.getAttribute('data-heading');

    this.setValues($.extend({},
      svgedit.utilities.getBBox(dom),
      {
        id,
        cls: dom.getAttribute('class') || 'textarea'
      }
    ));

    this.setOriginalFontSizeFromClass();
    this.fixOpacity(dom);

    this.parent_text = dom.getAttribute('data-parent') || null;
    this.child_text = dom.getAttribute('data-child') || null;
    this.smart_asset = dom.getAttribute(constants.smartAssets.attributes.definedAsset) || null;

    this.page_id = dom.dataset.pageId || INKIVE.Tool._current_design_id;

    this.text.setDOM(dom, shouldSkipGetChars);

    if (this.isSubelement() && !this.original_font_size) {
      this.original_font_size = this.text.font_size;
    }

    if (!this.text.original_font_size && this.original_font_size) {
      this.text.original_font_size = this.original_font_size;
    }

    // Set cache
    INKIVE.Text._cache[id] = this;

    return this;
  }

  setCursorTextType (isEdit) {
    if (!this.dom) {
      return;
    }

    $(this.dom).css('cursor', isEdit ? 'text' : 'default');
    $(this.dom.getElementsByTagName('rect')[0]).css('cursor', isEdit ? 'text' : 'default');
    $(this.dom.getElementsByTagName('text')[0]).css('cursor', isEdit ? 'text' : 'default');
  }

  isSubelement () {
    // Special case for grouped text only
    const parent = this.dom.parentElement;
    const hasNonTextareaElements = parent?.childNodes && [...parent?.childNodes].some(element => !(element.tagName === 'g' && element?.classList?.contains('textarea')));
    if (!hasNonTextareaElements && !parent?.matches?.('.sticker') && !parent?.matches?.('.table-cell')) {
      parent?.querySelector('.subelement')?.classList?.remove('subelement');
      return false;
    }

    return this.dom.getAttribute('class').indexOf('subelement') > -1;
  }

  getBBox () {
    return $.extend({}, svgedit.utilities.getBBox(this.dom));
  }

  getCRect () {
    const rect = this.dom.getElementsByTagName('rect')[0];
    return $.extend({}, rect.getBoundingClientRect());
  }

  getWidth () {
    return this.getCRect().width;
  }

  getHeight () {
    const rect = this.dom.getElementsByTagName('rect')[0];
    return this.new_rect_height || $(rect).height() || this.getCRect().height;
  }

  getBottom () {
    return this.new_rect_bottom || this.getCRect().bottom;
  }

  getY () {
    return this.getCRect().y;
  }

  getBBoxAligned () {
    const bb = this.getBBox();
    const modifier = this.text.direction === 'rtl'
      ? -1
      : 1;

    switch (this.text.text_anchor) {
      case 'middle':
        bb.x = bb.x + bb.width / 2 * modifier;
        break;
      case 'end':
        bb.x = bb.x + bb.width * modifier;
        break;
    }

    return bb;
  }

  invalidate () {
    INKIVE.Text._cache[this.id] = null;
  }

  /**
   *
   * @param canvas
   * @param {{allowResizeBelowMinValue: boolean}} options
   */
  render (canvas, options) {
    const that = this;

    this.invalidate();

    let rect;
    let parentElement;
    if (!this.dom) {
      const attr = {
        id: this.id ? this.id : canvas.getNextId(),
        class: this.cls
      };

      if (this.smart_asset) {
        attr[constants.smartAssets.attributes.definedAsset] = this.smart_asset;
      }

      if (this.definedContentType) {
        attr[constants.smartAssets.attributes.definedContentType] = this.definedContentType;
      }

      if (this.meta) {
        attr['data-meta'] = this.meta;
        if (this.meta === headerAndFooter.heading.attribute.value) {
          const section = INKIVE.EditorFacade.get('header_and_footer', 'range').headingSection;
          const elementId = headerAndFooter[section]?.id;
          parentElement = document.getElementById(elementId ?? headerAndFooter.header.id);
          attr.id = this.id ?? canvas.getNextId(parentElement);
        }
      }

      if (this.heading) {
        attr['data-heading'] = this.heading;
      }

      this.dom = canvas.addSvgElementFromJson({ element: 'g', attr }, parentElement);
    }

    rect = this.dom.getElementsByTagName('rect')[0];

    if (!rect) {
      rect = canvas.addSvgElementFromJson({
        element: 'rect',
        curStyles: false,
        attr: {
          id: canvas.getNextId(parentElement),
          x: this.x,
          y: this.y,
          width: this.width,
          height: this.height,
          stroke: 'none',
          fill: 'none'
        }
      }, this.dom);
    }

    const attributes = $.map(this.dom.attributes, function (item) {
      return item.name;
    });

    const autoAdjustableTextPosition = that.text.auto_adjustable ? $(that.text.dom).find('text').attr('y') : null;

    const { userInput, userInputSmartAsset, definedAsset, definedContentType } = constants.smartAssets.attributes;
    const ignoredAttributes = [
      definedContentType,
      userInput,
      userInputSmartAsset,
      definedAsset,
      'class',
      'data-child',
      'data-heading',
      'data-meta',
      'data-page-id',
      'data-parent',
      'id',
      'transform',
      'opacity'
    ];

    if (that.parent_text) {
      that.dom.dataset.parent = that.parent_text;
    }

    if (that.child_text) {
      that.dom.dataset.child = that.child_text;
    }

    if (that.page_id) {
      that.dom.dataset.pageId = that.page_id;
    }

    if (that.smart_asset) {
      that.dom.dataset.definedPlaceholder = that.smart_asset;
    }

    if (that.heading) {
      that.dom.dataset.heading = that.heading;
    } else {
      that.dom.removeAttribute('data-heading');
    }

    that.dom.className.baseVal = that.dom.className?.baseVal.replace(/original-font-size-.+?/g, '');
    if (that.original_font_size) {
      $(that.dom).addClass('original-font-size-' + that.original_font_size);
    }
    const firstDiffLinePosition = that.text.firstDiffLinePosition;

    firstDiffLinePosition === null && $(this.dom).find(':not(rect)').remove();

    const initialNumOfLines = that.text.strings.length;

    const text = that.text.render(canvas, this);
    autoAdjustableTextPosition && $(that.text.dom).find('text').attr('y', autoAdjustableTextPosition);

    const currentNumOfLines = that.text.strings.length;

    const textBBox = text.getBBox();
    attributes.forEach(item => {
      if (!ignoredAttributes.includes(item)) {
        that.dom.removeAttribute(item);
      }
    });
    const letterWidths = [textBBox.width];
    const isTableText = this.dom && this.dom.parentElement && this.dom.parentElement.classList.contains('table-cell');

    if (!isTableText && !that.first_load && (that.text.auto_adjustable || that.library_text)) {
      const textWidth = Math.max(...letterWidths);
      const extraWidth = textWidth * this.extra_width_factor;
      rect.setAttribute('width', textWidth + extraWidth);
      rect.setAttribute('y', text.getBBox().y);
      rect.setAttribute('x', text.getBBox().x);

      that.library_text = false;
    }
    const offsetHeight = textBBox.y - Number.parseFloat(rect.getAttribute('y'));
    const newRectHeight = isNaN(offsetHeight) ? textBBox.height : textBBox.height + offsetHeight;
    const initialHeight = Number(rect.getAttribute('height'));
    const isSelected = canvas.getSelectedElems().includes(this.dom);

    // For table texts, we handle differently
    if (isTableText) {
      // Resize text rect only if there is not enough room
      const deltaChange = INKIVE.Table.adaptRowHeightToContent(this.dom.parentElement, { allowResizeBelowMinValue: options?.allowResizeBelowMinValue ?? false });
      if (deltaChange !== 0) {
        that.text.render(canvas, this);
      }
    } else if (that.child_text) {
      const toUpdateHeight = initialHeight < newRectHeight ? newRectHeight : initialHeight;
      const rectHeight = this.new_rect_height || toUpdateHeight;
      rect.setAttribute('height', rectHeight);
    } else {
      const rectHeight = this.new_rect_height || newRectHeight;
      rect.setAttribute('height', Math.max(parseFloat(rectHeight), 0));
    }

    if (isSelected) {
      this.text.characterBBoxes = this.text.getChars();
    }

    // if (initialNumOfLines !== currentNumOfLines || that.first_load) {
    if (isSelected && !this.typing && !isTableText) {
      that.text.vertical_offset || svgedit.select.getSelectorManager().requestSelector(this.dom).resize();
    } else if (this.typing && initialNumOfLines !== currentNumOfLines && !isTableText) {
      svgedit.select.getSelectorManager().requestSelector(this.dom).resize();
    }

    this.new_rect_height = null;
    this.new_rect_bottom = null;
    return this.dom;
  }

  moveTextByY (canvas, dy) {
    if (!dy || !this.dom || this.dom.parentElement.classList.contains('table-cell')) return;

    const rect = this.dom.getElementsByTagName('rect')[0];
    const xform = canvas.getRootElem().createSVGTransform();
    const tlist = svgedit.transformlist.getTransformList(rect);

    xform.setTranslate(0, dy);

    if (tlist.numberOfItems) {
      tlist.replaceItem(xform, 0);
    } else {
      tlist.appendItem(xform);
    }

    canvas.recalculateDimensions(rect);
    this.setValues($.extend({}, rect.getBBox()));
  }
}
